import { useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { pick } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Header from 'components/Header'
import Confirm from 'components/Dialog'
import { SubmitButton } from 'components/Buttons'
import { DeleteIcon } from 'components/Icons'
import PhoneList from 'pages/Member/Contact/Form/PhoneList'
import { sequencePhones } from 'utils/phone'
import { destroy as destroyContact, save as saveContact } from 'store/academic/parent/contact'

const titles = ['Mr.', 'Ms.', 'Mrs.']

const initialValues = {
  relationship: '',
  contact: {
    type: 'contact',
    title: null,
    firstName: null,
    lastName: null,
    phones: [{ type: 'home', number: '' }],
  },
  pickup: false,
  emergency: false,
}

const schema = yup.object().shape({
  relationship: yup.string().required('Required'),
  contact: yup.object().shape({
    title: yup.string().nullable(),
    firstName: yup.string().required('Required'),
    lastName: yup.string().required('Required'),
  }),
})


export default function ParentContactForm({ memberId, data, seq, onClose, onSaved }) {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [confirm, setConfirm] = useState(false)
  const [saving, setSaving] = useState(false)

  const relationshipTypes = useSelector(state => state.lookup.relationshipTypes)

  const { control, handleSubmit, getValues } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ...initialValues,
      ...data,
      relationship: data?.relationship ?? '',
      seq: data?.seq ?? seq,
    },
  })

  const phoneArrayControl = useFieldArray({ control, name: 'contact.phones', keyName: 'uuid' })

  const onSubmit = async formData => {
    const payload = {
      ...pick(formData, ['id', 'relationship', 'seq', 'pickup', 'emergency']),
      isGuardian: false,
      contact: {
        ...pick(formData.contact, ['title', 'firstName', 'lastName']),
        type: 'contact',
        phones: sequencePhones(
          (formData.contact?.phones || []).filter(phone => phone?.number).map(phone => pick(phone, ['id', 'type', 'number'])),
        ),
      },
    }
    if (data?.contact?.id) payload.contact.id = data.contact.id

    setSaving(true)
    const res = await dispatch(saveContact({ memberId, ...payload }))
    setSaving(false)
    if (res.payload) onSaved()
  }

  const onDestroy = async () => {
    setConfirm(false)
    const res = await dispatch(destroyContact({ memberId, id: getValues()?.id }))
    if (!res.error) onSaved()
  }

  if (!relationshipTypes) return null

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ py: 2, overflowY: 'auto' }}>
      <Header title={t('Additional Contact Information')} small close={onClose} borderBottom />

      <Box sx={{ px: 3, pt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Controller
              name="relationship"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  size="small"
                  fullWidth
                  select
                  label={t('Relationship')}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  {...field}
                >
                  {relationshipTypes.map(item => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="contact.title"
              control={control}
              render={({ field }) => (
                <TextField size="small" fullWidth select label={t('Title')} {...field} value={field.value ?? ''}>
                  {titles.map(title => (
                    <MenuItem key={title} value={title}>
                      {title}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>

          {[
            { name: 'contact.firstName', label: t('First Name'), xs: 6 },
            { name: 'contact.lastName', label: t('Last Name'), xs: 6 },
          ].map(field => (
            <Grid item xs={field.xs} key={field.name}>
              <Controller
                name={field.name}
                control={control}
                render={({ field: props, fieldState }) => (
                  <TextField
                    size="small"
                    fullWidth
                    label={field.label}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    {...props}
                    value={props.value ?? ''}
                  />
                )}
              />
            </Grid>
          ))}


          <Grid item xs={12}>
            <PhoneList control={control} path="contact.phones" minCount={1} {...phoneArrayControl} />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth sx={{ p: 1, pl: 2, borderRadius: '4px', border: '1px solid rgba(0, 0, 0, 0.12)' }}>
              <Typography>Notifications</Typography>
              <Stack flexDirection={'column'} sx={{ ml: 2 }}>

                <Grid container>
                  {[
                    { name: 'pickup', label: t('Pickup') },
                    { name: 'emergency', label: t('Emergency Call') },
                  ].map(field => (
                    <Grid item xs={12} key={field.name}>
                      <Controller
                        name={field.name}
                        control={control}
                        render={({ field: props }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!props.value}
                                onChange={e => props.onChange(e.target.checked)}
                              />
                            }
                            label={field.label}
                          />
                        )}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            </FormControl>
          </Grid>

        </Grid>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, px: 3, pt: 3 }}>
        {data?.id ? (
          <Button color="error" size="small" onClick={() => setConfirm(true)} variant="outlined">
            <DeleteIcon /> {t('DELETE CONTACT')}
          </Button>
        ) : (
          <Box />
        )}
        <SubmitButton size="small" color="primary" title={t('SAVE')} disabled={saving} onClick={handleSubmit(onSubmit)} />
      </Box>

      <Confirm open={confirm} onClose={() => setConfirm(false)} onConfirm={onDestroy} />
    </Box>
  )
}
